import { isEmpty, isEqual, when } from "util/support";
import type { ReviewableFormInputProps } from "../types";
import type { ReviewableInput } from "ui/widgets/Input";
import useFieldsStatuses from "./useFieldsStatuses";
import { useConfigContext } from "../providers";

export const useRequestedChangeFields = ({
    setFieldValue,
    setFieldTouched,
    values,
    name
}: Pick<
    ReviewableFormInputProps,
    | 'setFieldValue'
    | 'setFieldTouched'
    | 'values'
    | 'name'
>) => {
    const config = useConfigContext();

    const { changeFieldStatus } = useFieldsStatuses({
        setFieldValue,
        setFieldTouched,
        values,
        name
    });

    const getComments = (fallback = []) => values[config.RequestedChangeFields]?.[name]?.comments ?? fallback;

    const hasChanges = (comments: Array<string> = []) => !isEqual(
        getComments(),
        comments
    );

    const onSubmitComments: ReviewableInput.ReviewableInputProps['onSubmitComments'] = ({ comments }) =>
        when(
            () => hasChanges(comments),
            () => {
                const value = {
                    ...values[config.RequestedChangeFields],
                    [name]: {
                        comments
                    }
                };

                when(
                    comments.length,
                    () => changeFieldStatus(config.FieldStatusRequestedChanges),
                    () => {
                    delete value[name];
                    changeFieldStatus(config.FieldStatusAwaitingReview);
                });

                setFieldValue(
                    config.RequestedChangeFields,
                    when(
                        isEmpty(value),
                        null,
                        value
                    )
                );

                setTimeout(() => setFieldTouched(config.RequestedChangeFields, true, true));
            });

    const onAddComment: ReviewableInput.ReviewableInputProps['onAddComment'] = () => {
        // No-op
    };

    const onChange: ReviewableInput.ReviewableInputProps['onChange'] = () => {
        // No-op
    };

    return {
        getComments,
        onSubmitComments,
        onAddComment,
        onChange
    };
};

export default useRequestedChangeFields;
